import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Row } from '@opendoor/bricks-next';

import type { MegaNavItem } from '../../types';
import GlassMegaNavFlyout from './GlassMegaNavFlyout';
import { GlassNavLink } from './GlassNavLink';

interface GlassMegaNavProps {
  slug: string;
  megaNavData?: MegaNavItem[];
  onOpenChange?: (itemName: string | null) => void;
}

export const GlassMegaNav: React.FC<GlassMegaNavProps> = ({ slug, megaNavData, onOpenChange }) => {
  const [openNavItem, setOpenNavItem] = useState<string | null>(null);
  const navRef = useRef<HTMLDivElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  const handleToggle = useCallback(
    (itemName: string) => {
      const next = openNavItem === itemName ? null : itemName;
      if (next !== null) {
        openerRef.current = document.activeElement as HTMLElement;
      } else {
        openerRef.current = null;
      }
      setOpenNavItem(next);
      onOpenChange?.(next);
    },
    [openNavItem, onOpenChange],
  );

  const handleClose = useCallback(() => {
    setOpenNavItem(null);
    onOpenChange?.(null);
    openerRef.current?.focus();
    openerRef.current = null;
  }, [onOpenChange]);

  useEffect(() => {
    if (!openNavItem) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        handleClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [openNavItem, handleClose]);

  const activeItem = useMemo(
    () => megaNavData?.find((item) => item.name === openNavItem && item.sections?.length),
    [megaNavData, openNavItem],
  );

  return (
    <Box
      display="block"
      ref={navRef}
      position="relative"
      $platform-web={{ gridColumn: '2 / 3', gridRow: '1 / 2' }}
    >
      <Row
        m="$0"
        tag="ul"
        $smallerThanMD={{ display: 'none' }}
        justifyContent="flex-end"
        alignItems="center"
        gap="$1half"
        analyticsName="cosmos-glass-nav-menu-list"
        width="100%"
        $platform-web={{ listStyleType: 'none' }}
      >
        {megaNavData?.map((navItem) => {
          const hasSections = navItem.sections && navItem.sections.length > 0;
          const isOpen = openNavItem === navItem.name;

          return (
            <Box tag="li" display="block" key={navItem.name} position="relative">
              <GlassNavLink
                name={navItem.name}
                href={navItem.href}
                hasDropdown={hasSections}
                isOpen={isOpen}
                active={slug === navItem.href}
                onPress={hasSections ? () => handleToggle(navItem.name) : undefined}
              />
            </Box>
          );
        })}
      </Row>
      <GlassMegaNavFlyout item={activeItem ?? null} />
    </Box>
  );
};
